import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * Cinematic auto-scroll tour played once per session, only on the home page.
 * Waits for the intro overlay to be dismissed, then slowly scrolls through
 * the main sections in sequence — like a video playing.
 * User can cancel anytime by scrolling manually or clicking the skip button.
 */
const STEPS = [
  { id: "overview", hold: 1800, label: "01 · Panoramica" },
  { id: "preorder-notice-anchor", hold: 2200, label: "02 · Preordini" },
  { id: "showreel", hold: 2400, label: "03 · Showreel" },
  { id: "xbox", hold: 1800, label: "04 · Xbox Partner" },
  { id: "releases", hold: 1800, label: "05 · Prossime uscite" },
  { id: "chapter-02", hold: 2000, label: "06 · Chapter 02" },
  { id: "chapter-03", hold: 2000, label: "07 · Chapter 03" },
  { id: "editions", hold: 2200, label: "08 · Edizioni" },
  { id: "faq", hold: 1800, label: "09 · FAQ preordine" },
  { id: "store", hold: 2000, label: "10 · Store" },
  { id: "dlc", hold: 1800, label: "11 · DLC" },
  { id: "about", hold: 1800, label: "12 · Studio" },
];

export default function CinematicTour() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (location.pathname !== "/") return;
    try {
      if (sessionStorage.getItem("ashvolt-tour-seen") === "1") return;
    } catch {}
    // Start the auto-scroll tour shortly after the site loads.
    const start = setTimeout(() => setActive(true), 1400);
    return () => clearTimeout(start);
  }, [location.pathname]);

  useEffect(() => {
    if (!active || cancelled) return;
    if (step >= STEPS.length) {
      finalize(true);
      return;
    }
    const target = document.getElementById(STEPS[step].id);
    if (target && window.__lenis) {
      window.__lenis.scrollTo(target, { offset: -70, duration: 2.2, easing: (t) => 1 - Math.pow(1 - t, 3) });
    } else if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const t = setTimeout(() => setStep((s) => s + 1), STEPS[step].hold);
    return () => clearTimeout(t);
  }, [active, step, cancelled]);

  useEffect(() => {
    if (!active || cancelled) return;
    let touched = false;
    const cancel = () => {
      if (touched) return;
      touched = true;
      setCancelled(true);
      finalize(false);
    };
    window.addEventListener("wheel", cancel, { passive: true, once: true });
    window.addEventListener("touchstart", cancel, { passive: true, once: true });
    return () => {
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
    };
  }, [active, cancelled]);

  function finalize(scrollTop) {
    try {
      sessionStorage.setItem("ashvolt-tour-seen", "1");
    } catch {}
    setActive(false);
    if (scrollTop && window.__lenis) {
      window.__lenis.scrollTo(0, { duration: 2.2 });
    }
  }

  if (!active || cancelled) return null;

  const current = STEPS[Math.min(step, STEPS.length - 1)];

  return (
    <div
      data-testid="cinematic-tour"
      className="fixed bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-[350] pointer-events-none"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto flex items-center gap-4 md:gap-6 border border-white/15 bg-black/70 backdrop-blur-xl px-5 md:px-6 py-3 md:py-4"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.35em] text-accent">
              Tour
            </span>
          </div>
          <span className="w-px h-4 bg-white/20" />
          <span className="font-heading text-sm md:text-base uppercase tracking-[0.15em] text-white whitespace-nowrap">
            {current.label}
          </span>
          <span className="w-px h-4 bg-white/20" />
          <button
            type="button"
            data-testid="tour-skip-btn"
            onClick={() => { setCancelled(true); finalize(false); }}
            className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-white/60 hover:text-accent transition-colors"
          >
            Salta
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
